import {ModulesPanelTabApi} from '../api/ModulesPanelTabApi';
import {BaseValidatedClass} from '../BaseValidatedClass';

interface ModulesPanelTabMap {
  getId(): string;
  getIcon(): string;
  getPosition(): string;
  getTabIndex(): number;
  getName(): string;
  isEnabled(): boolean;
  getTemplate(): string;

  onDocumentChanged(): void;
  onRender(): void;
}

export class ModulesPanelTab extends BaseValidatedClass {
  /** Provides access to editor functionalities specific to this tab instance. */
  public api!: ModulesPanelTabApi;

  /** List of methods that must be implemented by subclasses */
  private static readonly REQUIRED_METHODS: (keyof ModulesPanelTabMap)[] = ['getId', 'getIcon', 'getName', 'getTemplate', 'getTabIndex'];

  constructor() {
    super(ModulesPanelTab.REQUIRED_METHODS, ModulesPanelTab);
  }

  /**
   * Gets the unique identifier for this tab.
   * This ID is used for registration.
   * @returns A unique string ID.
   */
  public getId(): string {
    throw new Error('Method getId() must be implemented by the subclass');
  }

  /**
   * Gets the icon key representing this tab in the header.
   * @returns A string representing the icon key from the IconsRegistry
   */
  public getIcon(): string {
    throw new Error('Method getIcon() must be implemented by the subclass');
  }

  /**
   * Retrieves the index of the tab associated with the panel.
   * The index represents the position/order of the tab in the UI.
   *
   * @returns {number} The index of the tab.
   */
  public getTabIndex(): number {
    throw new Error('Method getTabIndex() must be implemented by the subclass');
  }

  /**
   * Gets the display name of the tab shown to the user in the header hint.
   * Use `this.api.translate()` for localization.
   * @returns The localized tab name string.
   */
  public getName(): string {
    throw new Error('Method getName() must be implemented by the subclass');
  }

  /**
   * Determines if the tab should be available for use in the editor.
   * Override to provide custom logic based on the editor state or configuration.
   * @returns True if the tab is enabled, false otherwise. Defaults to true.
   */
  public isEnabled(): boolean {
    return true;
  }

  /**
   * Gets the HTML template string that defines the initial structure of this tab.
   * @returns An HTML string.
   */
  public getTemplate(): string {
    throw new Error('Method getTemplate() must be implemented by the subclass');
  }

  /**
   * Optional hook called when the modules panel tab is initially rendered.
   * Use this for setup tasks like attaching event listeners to the panel's template elements.
   */
  public onRender(): void {
  }

  /**
   * Lifecycle hook called when any part of the document template has changed.
   * This can be frequent; use cautiously for performance-sensitive operations.
   */
  public onDocumentChanged(): void {
  }
}
